<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import KanbanCard from 'dashboard/components-next/Kanban/KanbanCard.vue';

const props = defineProps({
  stage: { type: String, required: true },
  contacts: { type: Array, required: true },
  count: { type: Number, default: 0 },
  isLoading: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  dragEnabled: { type: Boolean, default: true },
  fullWidth: { type: Boolean, default: false },
});

const emit = defineEmits(['move', 'loadMore']);

const { t } = useI18n();

// Only the header gets an accent — mapped onto Chatwoot's own existing
// label color tokens (slate/amber/teal/ruby/blue/iris), not new colors.
const STAGE_ACCENTS = {
  New: {
    dot: 'bg-n-blue-9',
    border: 'border-t-n-blue-9',
    icon: 'i-lucide-sparkles',
  },
  Contacted: {
    dot: 'bg-n-blue-9',
    border: 'border-t-n-blue-9',
    icon: 'i-lucide-phone-call',
  },
  Qualified: {
    dot: 'bg-n-teal-9',
    border: 'border-t-n-teal-9',
    icon: 'i-lucide-badge-check',
  },
  'Meeting Scheduled': {
    dot: 'bg-n-iris-9',
    border: 'border-t-n-iris-9',
    icon: 'i-lucide-calendar',
  },
  'Proposal Sent': {
    dot: 'bg-n-amber-9',
    border: 'border-t-n-amber-9',
    icon: 'i-lucide-file-text',
  },
  Negotiation: {
    dot: 'bg-n-ruby-9',
    border: 'border-t-n-ruby-9',
    icon: 'i-lucide-handshake',
  },
  Won: {
    dot: 'bg-n-teal-9',
    border: 'border-t-n-teal-9',
    icon: 'i-lucide-trophy',
  },
  Lost: {
    dot: 'bg-n-slate-9',
    border: 'border-t-n-slate-9',
    icon: 'i-lucide-circle-x',
  },
};

const accent = computed(() => STAGE_ACCENTS[props.stage] || STAGE_ACCENTS.New);

const isDragOver = ref(false);

const onChange = event => {
  if (!event.added) return;
  const contact = event.added.element;
  const fromStage = contact.custom_attributes?.stage;
  if (fromStage && fromStage !== props.stage) {
    emit('move', { contact, fromStage, toStage: props.stage });
  }
};

const onDragEnter = () => {
  isDragOver.value = true;
};
const onDragLeave = () => {
  isDragOver.value = false;
};
const onDragEnd = () => {
  isDragOver.value = false;
};
</script>

<template>
  <div
    class="flex flex-col h-full overflow-hidden transition-colors duration-150 border rounded-lg bg-n-solid-1 md:max-h-[calc(100vh-260px)]"
    :class="[
      fullWidth ? 'w-full' : 'flex-shrink-0 w-80',
      isDragOver ? 'border-n-brand bg-n-brand/5' : 'border-n-weak',
    ]"
  >
    <div class="flex-shrink-0 h-1" :class="accent.dot" />
    <div
      class="flex items-center justify-between flex-shrink-0 px-3 py-2.5 border-b border-n-weak"
    >
      <div class="flex items-center min-w-0 gap-1.5">
        <span class="flex-shrink-0 rounded-full size-1.5" :class="accent.dot" />
        <Icon
          :icon="accent.icon"
          class="flex-shrink-0 size-3.5 text-n-slate-10"
        />
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ stage }}
        </span>
      </div>
      <span
        class="flex items-center justify-center h-5 px-1.5 text-xs rounded-full text-n-slate-11 bg-n-alpha-2 min-w-5"
      >
        {{ count }}
      </span>
    </div>

    <div class="flex flex-col flex-1 min-h-0 gap-3 p-3 overflow-y-auto">
      <Draggable
        v-if="dragEnabled"
        :list="contacts"
        group="kanban-cards"
        item-key="id"
        handle=".kanban-drag-handle"
        animation="180"
        ghost-class="kanban-ghost"
        chosen-class="kanban-chosen"
        drag-class="kanban-dragging"
        class="flex flex-col flex-1 min-h-0 gap-3"
        @change="onChange"
        @dragenter="onDragEnter"
        @dragleave="onDragLeave"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <KanbanCard :contact="element" />
        </template>
      </Draggable>
      <div v-else class="flex flex-col gap-3">
        <KanbanCard
          v-for="contact in contacts"
          :key="contact.id"
          :contact="contact"
        />
      </div>

      <div
        v-if="!isLoading && contacts.length === 0"
        class="flex flex-col items-center justify-center gap-2 py-8 text-center"
      >
        <Icon icon="i-lucide-inbox" class="size-6 text-n-slate-8" />
        <span class="text-sm text-n-slate-10">
          {{ t('KANBAN.EMPTY_COLUMN') }}
        </span>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center py-4">
        <Spinner />
      </div>

      <Button
        v-if="hasMore && !isLoading"
        :label="t('KANBAN.LOAD_MORE')"
        variant="faded"
        color="slate"
        size="xs"
        class="w-full"
        @click="emit('loadMore', stage)"
      />
    </div>
  </div>
</template>

<style scoped>
:deep(.kanban-ghost) {
  opacity: 0.5;
  border: 1.5px dashed var(--n-slate-7, #cbd5e1);
  border-radius: 0.5rem;
  background: var(--n-alpha-1, rgba(0, 0, 0, 0.02));
}

:deep(.kanban-chosen) {
  opacity: 0.9;
}

:deep(.kanban-dragging) {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  transform: rotate(1.5deg);
  cursor: grabbing;
}
</style>
